import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useUserContext } from '@/context_providers/user_context/UserProvider';
import Loading from '@/components/loading/Loading';
import {
  addAppVersion,
  actuallyGetAllByObjectType,
  updateAppVersion,
  deleteAppVersion,
} from '@/repository/AppVersionDAO';
import { convertTimestampToLocalDateTime } from '@/utils/date_convert';
import {
  useToast,
  ToastType,
} from '@/context_providers/toast_context/ToastContext';
import CreateAppVersionModal from './modals/CreateAppVersionModal';
import EditAppVersionModal from './modals/EditAppVersionModal';
import LoadingSpinner from '@/components/loading/LoadingSpinner';
import COLORS from '@/resources/style/Themes';
import ConfirmModal from '@/components/modals/utils/ConfirmModal';
import { Android, Apple, Delete, Edit, Language } from '@mui/icons-material';

export const AppVersioning = () => {
  const [androidAppData, setAndroidAppData] = useState(null);
  const [iosAppData, setIosAppData] = useState(null);
  const [webAppData, setWebAppData] = useState(null);
  const { bearerToken } = useUserContext();
  const { showToast } = useToast();
  const [showAddVersionModal, setShowAddVersionModal] = useState(false);
  const [showEditAppVersionModal, setShowEditAppVersionModal] = useState(false);
  const [versionToEdit, setVersionToEdit] = useState();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');
  const [versionToRemove, setVersionToRemove] = useState();
  const [messageToHighlight, setMessageToHighlight] = useState(null);
  const [objectType, setObjectType] = useState('version_android');

  useEffect(() => {
    const fetchAppVersionData = async () => {
      try {
        const androidResult =
          await actuallyGetAllByObjectType('version_android');
        setAndroidAppData(androidResult);
      } catch (e) {
        showToast(
          'Failed to fetch Android version data. Please try again.',
          ToastType.DANGER,
        );
      }
      try {
        const iOSResult = await actuallyGetAllByObjectType('version_ios');
        setIosAppData(iOSResult);
      } catch (e) {
        showToast(
          'Failed to fetch iOS version data. Please try again.',
          ToastType.DANGER,
        );
      }
      try {
        const webResult = await actuallyGetAllByObjectType('version_web_app');
        setWebAppData(webResult);
      } catch (e) {
        showToast(
          'Failed to fetch Web version data. Please try again.',
          ToastType.DANGER,
        );
      }
    };
    fetchAppVersionData();
  }, [bearerToken]);

  const addVersionLocal = async (newVersion) => {
    try {
      const newData = await addAppVersion(newVersion);
      showToast(`Successfully added ${newVersion.version}.`, ToastType.SUCCESS);
      if (newData.object_type === 'version_android') {
        setAndroidAppData((prevData) => [...prevData, newData]);
      } else if (newVersion.object_type === 'version_ios') {
        setIosAppData((prevData) => [...prevData, newData]);
      } else if (newVersion.object_type === 'version_web_app') {
        setWebAppData((prevData) => [...prevData, newData]);
      }
    } catch (error) {
      showToast(
        `Failed to add ${newVersion.version} for ${newVersion.object_type}. Please try again.`,
        ToastType.DANGER,
      );
      console.error(error);
    }
  };

  const deleteAppVersionLocal = async () => {
    try {
      setShowConfirmModal(false);

      await deleteAppVersion(versionToRemove);
      if (versionToRemove.object_type === 'version_android') {
        const filteredData = androidAppData.filter(
          (version) => version.id !== versionToRemove.id,
        );
        setAndroidAppData(filteredData);
      } else if (versionToRemove.object_type === 'version_ios') {
        const filteredData = iosAppData.filter(
          (version) => version.id !== versionToRemove.id,
        );
        setIosAppData(filteredData);
      } else if (versionToRemove.object_type === 'version_web_app') {
        const filteredData = webAppData.filter(
          (version) => version.id !== versionToRemove.id,
        );
        setWebAppData(filteredData);
      }
      showToast(
        `Successfully removed message ${versionToRemove.version} for ${versionToRemove.object_type}.`,
        ToastType.SUCCESS,
      );
    } catch (e) {
      showToast(
        `Failed to remove version ${versionToRemove.version} for ${versionToRemove.object_type}. Please try again.`,
        ToastType.DANGER,
      );
    }
    setVersionToRemove();
  };

  const editAppVersionLocal = async (editedVersion) => {
    try {
      await updateAppVersion(editedVersion);
      setMessageToHighlight(editedVersion.id);
      showToast(
        `Successfully edited ${editedVersion.version}.`,
        ToastType.SUCCESS,
      );
      if (editedVersion.object_type === 'version_android') {
        const index = androidAppData.findIndex(
          (version) => version.id === editedVersion.id,
        );
        const updatedData = [...androidAppData];
        updatedData[index] = editedVersion;
        setAndroidAppData(updatedData);
      } else if (editedVersion.object_type === 'version_ios') {
        const index = iosAppData.findIndex(
          (version) => version.id === editedVersion.id,
        );
        const updatedData = [...iosAppData];
        updatedData[index] = editedVersion;
        setIosAppData(updatedData);
      } else if (editedVersion.object_type === 'version_web_app') {
        const index = webAppData.findIndex(
          (version) => version.id === editedVersion.id,
        );
        const updatedData = [...webAppData];
        updatedData[index] = editedVersion;
        setWebAppData(updatedData);
      }
    } catch (error) {
      showToast(
        `Failed to update ${editedVersion.version} for ${editedVersion.object_type}. Please try again.`,
        ToastType.DANGER,
      );
      console.error(error);
    }
  };

  useEffect(() => {
    if (messageToHighlight != null) {
      setTimeout(() => {
        setMessageToHighlight(null); // clear the id after a period of time (e.g. 2 seconds)
      }, 2000);
    }
  }, [messageToHighlight]);

  const buttonStyle = {
    borderRadius: '10px',
    height: '42px',
    paddingBottom: 3,
    textAlign: 'center',
    justifyContent: 'center',
    border: 'none',
  };

  const renderAppData = (title, appData) => {
    return (
      <Box>
        <Typography variant="h6">{title}</Typography>
        <TableContainer sx={{ boxShadow: 0 }} component={Paper}>
          <Table>
            <TableHead>
              <TableRow
                style={{
                  background:
                    title === 'Android'
                      ? COLORS.ANDROID_GREEN
                      : title === 'iOS'
                        ? COLORS.IPHONE_BLUE
                        : title === 'Web'
                          ? COLORS.WEB_PURPLE
                          : 'inherit',
                }}
              >
                <TableCell
                  sx={{ color: title === 'Android' ? 'inherit' : '#ffffff' }}
                >
                  Version
                </TableCell>
                <TableCell
                  sx={{ color: title === 'Android' ? 'inherit' : '#ffffff' }}
                >
                  Required
                </TableCell>
                <TableCell
                  sx={{ color: title === 'Android' ? 'inherit' : '#ffffff' }}
                >
                  Rollout
                </TableCell>
                <TableCell
                  sx={{ color: title === 'Android' ? 'inherit' : '#ffffff' }}
                >
                  Go Live
                </TableCell>
                <TableCell
                  sx={{ color: title === 'Android' ? 'inherit' : '#ffffff' }}
                >
                  Update Complete
                </TableCell>
                <TableCell
                  sx={{ color: title === 'Android' ? 'inherit' : '#ffffff' }}
                >
                  Notification Days
                </TableCell>
                <TableCell
                  sx={{ color: title === 'Android' ? 'inherit' : '#ffffff' }}
                >
                  Edit
                </TableCell>
                <TableCell
                  sx={{ color: title === 'Android' ? 'inherit' : '#ffffff' }}
                >
                  Delete
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appData.map((data, index) => (
                <TableRow key={index}>
                  <TableCell
                    className={
                      data.id === messageToHighlight ? 'flash-green' : ''
                    }
                  >
                    {data.version}
                  </TableCell>
                  <TableCell
                    className={
                      data.id === messageToHighlight ? 'flash-green' : ''
                    }
                  >
                    {data.required ? 'Yes' : 'No'}
                  </TableCell>
                  <TableCell
                    className={
                      data.id === messageToHighlight ? 'flash-green' : ''
                    }
                  >
                    {data.rollout_timestamp
                      ? convertTimestampToLocalDateTime(data.rollout_timestamp)
                      : 'N/A'}
                  </TableCell>
                  <TableCell
                    className={
                      data.id === messageToHighlight ? 'flash-green' : ''
                    }
                  >
                    {data.go_live.toString()}
                  </TableCell>
                  <TableCell
                    className={
                      data.id === messageToHighlight ? 'flash-green' : ''
                    }
                  >
                    {data.update_complete.toString()}
                  </TableCell>
                  <TableCell
                    className={
                      data.id === messageToHighlight ? 'flash-green' : ''
                    }
                  >
                    {data.notification_days && data.notification_days.length > 0
                      ? data.notification_days.join(', ')
                      : 'N/A'}
                  </TableCell>
                  <TableCell
                    className={
                      data.id === messageToHighlight ? 'flash-green' : ''
                    }
                  >
                    <IconButton
                      sx={{
                        borderRadius: '10px',
                        width: '40px',
                        height: '40px',
                        backgroundColor: COLORS.LIGHT_GREEN,
                        '&:hover': {
                          backgroundColor: COLORS.HOVER_GREEN,
                        },
                      }}
                      onClick={() => {
                        setVersionToEdit(data);
                        setShowEditAppVersionModal(true);
                      }}
                    >
                      <Edit sx={{ color: COLORS.DARK_GREEN }} />
                    </IconButton>
                  </TableCell>
                  <TableCell
                    className={
                      data.id === messageToHighlight ? 'flash-green' : ''
                    }
                  >
                    <IconButton
                      sx={{
                        borderRadius: '10px',
                        width: '40px',
                        height: '40px',
                        backgroundColor: COLORS.PINK,
                        '&:hover': {
                          backgroundColor: COLORS.HOVER_RED,
                        },
                      }}
                      onClick={() => {
                        setConfirmTitle(
                          `Delete ${data.version} for ${data.object_type}`,
                        );
                        setConfirmMessage(
                          `Are you sure you would like to delete version ${data.version} for ${data.object_type}?`,
                        );
                        setVersionToRemove(data);
                        setShowConfirmModal(true);
                      }}
                    >
                      <Delete sx={{ color: COLORS.DARK_RED }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Container maxWidth="xl">
      {androidAppData && iosAppData && webAppData ? (
        <>
          <Box justifyContent="space-between" alignItems="center" mb={3} mt={2}>
            <Typography variant="h5">App Versioning</Typography>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                sx={{
                  ...buttonStyle,
                  backgroundColor: COLORS.ANDROID_GREEN,
                  marginRight: '10px',
                }}
                startIcon={<Android />}
                onClick={() => {
                  setShowAddVersionModal(true);
                  setVersionToEdit(null);
                  setObjectType('version_android');
                }}
              >
                Add Android Version
              </Button>

              <Button
                variant="contained"
                sx={{
                  ...buttonStyle,
                  backgroundColor: COLORS.IPHONE_BLUE,
                  marginRight: '10px',
                }}
                startIcon={<Apple />}
                onClick={() => {
                  setShowAddVersionModal(true);
                  setVersionToEdit(null);
                  setObjectType('version_ios');
                }}
              >
                Add iOS Version
              </Button>

              <Button
                variant="contained"
                sx={{ ...buttonStyle, backgroundColor: COLORS.WEB_PURPLE }}
                startIcon={<Language />}
                onClick={() => {
                  setShowAddVersionModal(true);
                  setVersionToEdit(null);
                  setObjectType('version_web_app');
                }}
              >
                Add Web Version
              </Button>
            </Box>
          </Box>
          {renderAppData('Android', androidAppData)}
          <br></br>
          {renderAppData('iOS', iosAppData)}
          <br></br>
          {renderAppData('Web', webAppData)}
        </>
      ) : (
        <LoadingSpinner />
      )}

      <CreateAppVersionModal
        show={showAddVersionModal}
        onHide={() => setShowAddVersionModal(false)}
        onCreate={addVersionLocal}
        objectType={objectType}
      />

      <EditAppVersionModal
        show={showEditAppVersionModal}
        onHide={() => setShowEditAppVersionModal(false)}
        versionData={versionToEdit}
        onSave={editAppVersionLocal}
      />
      <ConfirmModal
        show={showConfirmModal}
        confirm={deleteAppVersionLocal}
        onHide={() => {
          setShowConfirmModal(false);
        }}
        confirmMessage={confirmMessage}
        confirmTitle={confirmTitle}
      />

      <Loading
        show={!androidAppData || !iosAppData || !webAppData}
        loadingText="Loading..."
      />
    </Container>
  );
};

export default withAuthenticationRequired(AppVersioning, {
  onRedirecting: () => <Loading loadingText={'Loading...'} />,
});
